import { AppContext } from '../app/context';

/** Zeigerereignis in Welt-/Bildschirmkoordinaten. */
export interface ToolPointer {
  x: number; // CSS-Pixel relativ zum Canvas
  y: number;
  ndcX: number;
  ndcY: number;
  ray: { origin: import('../core/vec').Vec3; dir: import('../core/vec').Vec3 };
  button: number;
  shift: boolean;
  ctrl: boolean; // Ctrl oder Cmd
  alt: boolean;
}

export interface Tool {
  readonly id: string;
  readonly label: string;
  readonly shortcut: string;
  readonly cursor: string;
  /** Standard-Kontexttipp für die Statusleiste. */
  readonly hint: string;

  enter(): void;
  exit(): void;
  pointerDown(e: ToolPointer): void;
  pointerMove(e: ToolPointer): void;
  pointerUp(e: ToolPointer): void;
  doubleClick(e: ToolPointer): void;
  /** Nicht als Shortcut/VCB abgefangene Tasten (z. B. Pfeiltasten). */
  keyDown(e: KeyboardEvent): void;
  /** Maßkasten-Eingabe mit Enter bestätigt. */
  applyVcb(raw: string): void;
  /** true, wenn getippte Zeichen als Maß erfasst werden sollen. */
  wantsVcb(): boolean;
  /** Aktuelle VCB-Anzeige (z. B. live Länge), null = Platzhalter. */
  vcbDisplay(): string | null;
  /** Esc. */
  cancel(): void;
  /** Laufende Aktion abbrechen (Undo, Zeigerabbruch, Projektwechsel) — ohne Esc-Nebenwirkungen. */
  abort(): void;
}

export abstract class ToolBase implements Tool {
  abstract readonly id: string;
  abstract readonly label: string;
  abstract readonly shortcut: string;
  abstract readonly cursor: string;
  abstract readonly hint: string;

  constructor(protected ctx: AppContext) {}

  enter(): void {}
  exit(): void {}
  pointerDown(_e: ToolPointer): void {}
  pointerMove(_e: ToolPointer): void {}
  pointerUp(_e: ToolPointer): void {}
  doubleClick(_e: ToolPointer): void {}
  keyDown(_e: KeyboardEvent): void {}
  applyVcb(_raw: string): void {}
  wantsVcb(): boolean { return false; }
  vcbDisplay(): string | null { return null; }
  cancel(): void {}
  abort(): void {
    this.cancel();
  }
}

/** Hilfsfunktion: Entfernung Cursor↔Punkt in px gilt als Klick ohne Drag. */
export function isClick(downX: number, downY: number, x: number, y: number, tolerance = 4): boolean {
  return Math.hypot(x - downX, y - downY) <= tolerance;
}

/**
 * Umschalt-Sperre wie SketchUp: Solange Umschalt gehalten wird, bleibt die beim
 * Drücken aktuelle Richtung (Achse/Ebene) gesperrt; Loslassen gibt sie frei.
 */
export class ShiftLock<T> {
  private value: T | null = null;

  /** Pro Zeigerbewegung aufrufen; `current` liefert die gerade gültige Richtung. */
  update(shift: boolean, current: () => T | null): T | null {
    if (!shift) {
      this.value = null;
      return null;
    }
    if (this.value === null) this.value = current();
    return this.value;
  }

  get locked(): T | null {
    return this.value;
  }

  reset(): void {
    this.value = null;
  }
}
